import { Button, Form, Input, Select, Space } from "antd";
import React, { ComponentProps, FC, memo, useCallback, use, useEffect, useMemo } from "react";
import { GQL } from "@binale-tech/shared";
import { FormattedMessage, useIntl } from "react-intl";
import { validation } from "scripts/infrastructure/helpers/validation";
import { ProductAccessUtils } from "../../../../scripts/models/utils/ProductAccessUtils";
import { KontoControlContext } from "../../../../scripts/context/accountingData/KontoEntitiesProvider";
import { Code, Switch } from "@heroui/react";
import { CompanyContext } from "../../../../scripts/context/CompanyContext";

const useGroupOptions = () => {
    const { companyGQL } = use(CompanyContext);
    const { bankList, kasseList } = companyGQL;

    return useMemo(() => {
        const banks: Record<string, { item: GQL.ICompanyBank; yearFrom: number }> = {};
        bankList.forEach(item => {
            if (!banks[item.id]) {
                banks[item.id] = { item, yearFrom: item.year };
            }
            if (item.year > banks[item.id].item.year) {
                banks[item.id] = { item, yearFrom: banks[item.id].yearFrom };
            }
        });
        const kbs: Record<string, { item: GQL.ICompanyKasse; yearFrom: number }> = {};
        kasseList.forEach(item => {
            if (!kbs[item.id]) {
                kbs[item.id] = { item, yearFrom: item.year };
            }
            if (item.year > kbs[item.id].item.year) {
                kbs[item.id] = { item, yearFrom: kbs[item.id].yearFrom };
            }
        });
        return { banks: Object.values(banks), kbs: Object.values(kbs) };
    }, [bankList, kasseList]);
};

type Props = {
    onClose: () => void;
    data?: GQL.IGenericRecordTextTemplate;
    disabled?: boolean;
};

const RecordTextTemplateForm: FC<Props> = ({ data, disabled, onClose }) => {
    const intl = useIntl();
    const { recordTextTemplateSave } = use(KontoControlContext);
    const [form] = Form.useForm<GQL.IGenericRecordTextTemplate>();
    const type = Form.useWatch(["type"], form);
    const productIds = Form.useWatch(["productIds"], form);
    const kbIds = Form.useWatch(["kbIds"], form);
    const bankIds = Form.useWatch(["bankIds"], form);

    const { banks, kbs } = useGroupOptions();

    const handleClose = useCallback(() => {
        form.resetFields();
        onClose();
    }, [form, onClose]);
    useEffect(() => {
        form.resetFields();
        form.setFieldsValue(data);
    }, [form, data]);

    const onFinish = useCallback(
        (values: GQL.IGenericRecordTextTemplate) => {
            recordTextTemplateSave({ ...values }).then(() => handleClose());
        },
        [recordTextTemplateSave, handleClose]
    );

    const textOpts =
        type === GQL.ITextTemplateType.Text1
            ? { maxLength: 60, translation: "app.fields.buchtext" }
            : { maxLength: 80, translation: "app.fields.buchtext2" };

    return (
        <Form<GQL.IGenericRecordTextTemplate>
            onFinish={onFinish}
            initialValues={data}
            form={form}
            layout="vertical"
            disabled={disabled}
        >
            <Form.Item<GQL.IGenericRecordTextTemplate> name="id" style={{ display: "none" }} />
            <Form.Item<GQL.IGenericRecordTextTemplate> name="type" style={{ display: "none" }} />
            <Form.Item<GQL.IGenericRecordTextTemplate> name="productIds" style={{ display: "none" }} />
            <Form.Item<GQL.IGenericRecordTextTemplate> name="bankIds" style={{ display: "none" }} />
            <Form.Item<GQL.IGenericRecordTextTemplate> name="kbIds" style={{ display: "none" }} />

            <Form.Item<GQL.IGenericRecordTextTemplate>
                name="text"
                label={<FormattedMessage id={textOpts.translation} />}
                rules={[validation.required(intl), validation.max(intl, textOpts.maxLength)]}
            >
                <Input style={{ width: "100%" }} showCount maxLength={textOpts.maxLength} />
            </Form.Item>

            <Form.Item<GQL.IGenericRecordTextTemplate> label={<FormattedMessage id="app.fields.products" />}>
                <Space direction="vertical">
                    {[...ProductAccessUtils.getAccountingKeys()].map(value => (
                        <Switch
                            key={value}
                            isSelected={productIds?.includes(value as GQL.IProductKey) ?? false}
                            onValueChange={isSelected => {
                                const selectedSet = new Set(productIds);
                                if (isSelected) {
                                    selectedSet.add(value as GQL.IProductKey);
                                } else {
                                    selectedSet.delete(value as GQL.IProductKey);
                                }
                                const newProductIds = [...ProductAccessUtils.getAccountingKeys()].filter(v =>
                                    selectedSet.has(v as GQL.IProductKey)
                                );

                                form.setFieldValue("productIds", newProductIds);
                                if (!selectedSet.has(GQL.IProductKey.Bank)) {
                                    form.setFieldValue("bankIds", []);
                                }
                                if (!selectedSet.has(GQL.IProductKey.Kb)) {
                                    form.setFieldValue("kbIds", []);
                                }
                            }}
                        >
                            <FormattedMessage id={"app.titles." + value} />
                        </Switch>
                    ))}
                </Space>
            </Form.Item>

            {productIds?.includes(GQL.IProductKey.Kb) && (
                <Form.Item<GQL.IGenericRecordTextTemplate> label={<FormattedMessage id="app.titles.KB.pl" />}>
                    <Space direction="vertical">
                        {kbs.map(v => (
                            <Switch
                                key={v.item.id}
                                isSelected={kbIds?.includes(v.item.id) ?? false}
                                onValueChange={isSelected => {
                                    // const newKbIds = isSelected ? [v.item.id] : [];
                                    if (isSelected) {
                                        form.setFieldValue("kbIds", Array.from(new Set([...(kbIds ?? []), v.item.id])));
                                    } else {
                                        form.setFieldValue(
                                            "kbIds",
                                            (kbIds ?? []).filter(id => id !== v.item.id)
                                        );
                                    }
                                }}
                            >
                                <Space>
                                    <Code>{v.item.accountNum}</Code>
                                    <span>{v.item.name}</span>
                                    <span>(seit {v.yearFrom})</span>
                                </Space>
                            </Switch>
                        ))}
                    </Space>
                </Form.Item>
            )}
            {productIds?.includes(GQL.IProductKey.Bank) && (
                <Form.Item<GQL.IGenericRecordTextTemplate> label={<FormattedMessage id="app.titles.Bank.pl" />}>
                    <Space direction="vertical">
                        {banks.map(v => (
                            <Switch
                                key={v.item.id}
                                isSelected={bankIds?.includes(v.item.id) ?? false}
                                onValueChange={isSelected => {
                                    // const newKbIds = isSelected ? [v.item.id] : [];
                                    if (isSelected) {
                                        form.setFieldValue(
                                            "bankIds",
                                            Array.from(new Set([...(bankIds ?? []), v.item.id]))
                                        );
                                    } else {
                                        form.setFieldValue(
                                            "bankIds",
                                            (bankIds ?? []).filter(id => id !== v.item.id)
                                        );
                                    }
                                }}
                            >
                                <Space>
                                    <Code>{v.item.accountNum}</Code>
                                    <span>{v.item.name}</span>
                                    <span>(seit {v.yearFrom})</span>
                                </Space>
                            </Switch>
                        ))}
                    </Space>
                </Form.Item>
            )}

            <Button type="primary" htmlType="submit">
                <FormattedMessage id={"app.button.save"} />
            </Button>
        </Form>
    );
};
export default memo(RecordTextTemplateForm);
